import React from 'react';
import { Check, Ban } from 'lucide-react';
import { formatPriceModifier } from '../../utils/formatCurrency';

/**
 * ModifierOption - Individual modifier option (radio or checkbox style)
 * 
 * Features:
 * - Large touch target (56px min height)
 * - Clear selected state
 * - Price delta display
 * - Unavailable/sold-out state
 * - Radio (single-select) or Checkbox (multi-select) mode
 */
export default function ModifierOption({
  option,
  selected = false,
  disabled = false,
  unavailable = false,
  selectionType = 'single', // 'single' | 'multi'
  onSelect,
  className = '',
}) {
  const {
    id,
    name,
    price = 0,
    description,
    is_available = true,
  } = option;

  const isUnavailable = unavailable || !is_available;
  const isDisabled = disabled || isUnavailable;

  const handleClick = () => {
    if (!isDisabled) {
      onSelect(option, !selected);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={isDisabled}
      className={`
        w-full min-h-[64px] px-4 py-3
        flex items-center gap-4
        rounded-xl border-2
        transition-all duration-150
        text-left
        ${selected && !isDisabled
          ? 'bg-[#f5a623]/10 border-[#f5a623]'
          : 'bg-[#1a1f26] border-white/10'
        }
        ${isUnavailable
          ? 'opacity-50 cursor-not-allowed'
          : isDisabled
            ? 'opacity-60 cursor-not-allowed'
            : 'hover:border-white/20 hover:bg-[#252b33] active:scale-[0.99]'
        }
        ${className}
      `}
      role={selectionType === 'single' ? 'radio' : 'checkbox'}
      aria-checked={selected}
      aria-disabled={isDisabled}
      aria-label={`${name}${price > 0 ? ` - add ${formatPriceModifier(price)}` : ''}${isUnavailable ? ' - unavailable' : ''}`}
    >
      {/* Selection Indicator */}
      <div className={`
        w-7 h-7 flex-shrink-0
        flex items-center justify-center
        rounded-${selectionType === 'single' ? 'full' : 'lg'}
        border-2 transition-all duration-150
        ${selected && !isDisabled
          ? 'bg-[#f5a623] border-[#f5a623]'
          : 'bg-transparent border-white/30'
        }
      `}>
        {selected && !isDisabled && (
          <Check size={16} className="text-[#0f1419]" strokeWidth={3} />
        )}
        {isUnavailable && (
          <Ban size={14} className="text-white/50" />
        )}
      </div>

      {/* Option Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`
            font-medium text-base
            ${isUnavailable ? 'text-white/50 line-through' : 'text-white'}
          `}>
            {name}
          </span>
          {isUnavailable && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-[#ef4444]/20 text-[#ef4444]">
              Sold Out
            </span>
          )}
        </div>
        {description && (
          <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">
            {description}
          </p>
        )}
      </div>

      {/* Price Delta */}
      {price > 0 && !isUnavailable && (
        <span className={`
          font-semibold text-base flex-shrink-0
          ${selected ? 'text-[#f5a623]' : 'text-gray-400'}
        `}>
          {formatPriceModifier(price)}
        </span>
      )}
      {price === 0 && !isUnavailable && (
        <span className="text-sm text-gray-500 flex-shrink-0">
          Free
        </span>
      )}
    </button>
  );
}
